import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Icono } from '@xiltrion/ui/icono';
import { Kbd, KbdGroup } from '@xiltrion/ui/kbd';
import { Button } from '@xiltrion/ui/button';
import { Input } from '@xiltrion/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@xiltrion/ui/toggle-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '@xiltrion/ui/tooltip';
import { Tabla, type ColumnaTabla, type OrdenTabla } from '@xiltrion/ui/bloques/tabla';
import { CERTIFIED_STATE_LABEL } from '@pqrs/contracts';
import type { CaseRow } from '@/api/types';
import { CHANNEL_LABEL, DueBadge, EmptyState, ErrorState, LoadingState, PageHeader, PriorityTag, RastreoTag, STATUS_LABEL, StatusTag, fmtDate, PRIORITY_LABEL } from '@/components/ui';
import { useTableColumns } from '@/features/profile/api';
import { useCasesInfinite } from './api';
import { COLUMNS, DEFAULT_COLUMNS, DEFAULT_SORT, QUICK_VIEWS, countActiveFilters, parseFilters, serverFilters, toSearchParams, type InboxFilters, type SavedView } from './inboxConfig';
import { InboxFilterBar } from './InboxFilters';
import { InboxBulkActions } from './InboxBulkActions';
import { FavoriteStar } from './FavoriteStar';
import { ImportantIcon } from '@/features/citizens/ImportantMark';
import { SavedViewsMenu } from './SavedViews';
import { NewCaseButton } from './NewCaseButton';
import { downloadCsv, notify } from './lib';
import { useCertifiedEmail } from '@/features/config/CertifiedEmailCard';

/**
 * Bandeja inteligente. Los filtros y el orden viven en la URL (vistas compartibles por enlace); las columnas, en el
 * perfil de la persona; las vistas guardadas, en el navegador. La tabla ordena y pagina contra el servidor.
 */
export function InboxPage({ title = 'Bandeja de solicitudes' }: { title?: string }) {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const filters = useMemo(() => parseFilters(sp), [sp]);
  const setFilters = useCallback((f: InboxFilters) => setSp(toSearchParams(f), { replace: true }), [setSp]);
  const [columns, setColumns] = useTableColumns('inbox', DEFAULT_COLUMNS);
  // Rastreo solo existe si la entidad configuró el certificado (Datacrédito Postal) que usan sus flujos: sin él, la columna no aparece.
  const certificado = !!useCertifiedEmail().data?.settings;
  const columnasActivas = useMemo(() => (certificado ? columns : columns.filter((k) => k !== 'rastreo')), [certificado, columns]);
  const [q, setQ] = useState(filters.q ?? '');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [focus, setFocus] = useState(-1);

  const query = useCasesInfinite(serverFilters(filters));
  const rows = useMemo(() => {
    const all = query.data?.pages.flatMap((p) => p.items) ?? [];
    if (!filters.from && !filters.to) return all;
    const from = filters.from ? new Date(`${filters.from}T00:00:00`).getTime() : -Infinity;
    const to = filters.to ? new Date(`${filters.to}T23:59:59`).getTime() : Infinity;
    return all.filter((c) => { const t = new Date(c.createdAt).getTime(); return t >= from && t <= to; });
  }, [query.data, filters.from, filters.to]);
  const selected = useMemo(() => rows.filter((r) => selectedIds.includes(r.id)), [rows, selectedIds]);

  // Search box → URL (debounced)
  useEffect(() => { setQ(filters.q ?? ''); }, [filters.q]);
  useEffect(() => {
    const t = window.setTimeout(() => { if ((filters.q ?? '') !== q.trim()) setFilters({ ...filters, q: q.trim() || undefined }); }, 350);
    return () => window.clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard navigation: j/k move, Enter opens, x toggles selection
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName) || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'j' || e.key === 'ArrowDown') { e.preventDefault(); setFocus((i) => Math.min(rows.length - 1, i + 1)); }
      else if (e.key === 'k' || e.key === 'ArrowUp') { e.preventDefault(); setFocus((i) => Math.max(0, i - 1)); }
      else if (e.key === 'Enter' && focus >= 0 && rows[focus]) { e.preventDefault(); nav(`/pqrs/${rows[focus]!.id}`); }
      else if (e.key === 'x' && focus >= 0 && rows[focus]) { e.preventDefault(); const id = rows[focus]!.id; setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id])); }
    };
    window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey);
  }, [rows, focus, nav]);

  // El orden es del servidor (URL `sort`/`dir`, necesario para paginar bien): la tabla solo marca la columna y avisa.
  const sortKey = filters.sort ?? DEFAULT_SORT.sort;
  const dir = filters.dir ?? DEFAULT_SORT.dir;
  const orden = useMemo<OrdenTabla | null>(() => { const c = COLUMNS.find((x) => x.sortField === sortKey); return c ? { key: c.key, desc: dir === 'desc' } : null; }, [sortKey, dir]);
  const onOrdenChange = (o: OrdenTabla | null) => {
    const c = o ? COLUMNS.find((x) => x.key === o.key) : undefined;
    setFilters({ ...filters, sort: c?.sortField ?? 'createdAt', dir: o?.desc ? 'desc' : 'asc' });
  };
  const exportCsv = (which: CaseRow[]) => {
    // La segunda línea de una columna (tipo, área) sale como columna propia en el CSV.
    const visible = columnasActivas.flatMap((k) => { const c = COLUMNS.find((x) => x.key === k); return c ? [{ key: c.key, header: c.header }, ...(c.secondary ? [c.secondary] : [])] : []; });
    downloadCsv(`pqrs-${new Date().toISOString().slice(0, 10)}.csv`, visible.map((c) => c.header), which.map((r) => visible.map((c) => cellText(r, c.key))));
    notify('success', `Exportados ${which.length} caso(s)`);
  };
  const applyView = (v: SavedView) => { setSp(new URLSearchParams(v.params), { replace: true }); setColumns(v.columns); notify('info', `Vista "${v.name}" aplicada`); };
  const activeCount = countActiveFilters(filters);
  const total = rows.length;

  const columnas = useMemo<ColumnaTabla<CaseRow>[]>(() => [
    { key: '_fav', titulo: '', valor: () => '', ordenable: false, fija: true, ancho: '2.5rem', render: (r) => <FavoriteStar caseId={r.id} label={r.radication ?? r.subject} /> },
    ...COLUMNS.filter((c) => c.key !== 'rastreo' || certificado).map<ColumnaTabla<CaseRow>>((c) => ({
      key: c.key, titulo: c.header, ancho: c.width, ordenable: !!c.sortField, unaLinea: c.oneLine, lineas: c.lines,
      valor: (r) => cellText(r, c.key), render: (r) => <Cell row={r} col={c.key} />,
      secundario: c.secondary ? (r) => cellText(r, c.secondary!.key) || null : undefined,
    })),
  ], [certificado]);

  return (
    <div className="flex flex-col gap-3">
      <PageHeader title={filters.view && QUICK_VIEWS[filters.view] ? `${title} · ${QUICK_VIEWS[filters.view]!.label}` : title}
        subtitle={<span>{query.isLoading ? 'Cargando…' : `${total}${query.hasNextPage ? '+' : ''} caso(s)`} · <KbdGroup><Kbd>j</Kbd>/<Kbd>k</Kbd></KbdGroup> navegar, <Kbd>Enter</Kbd> abrir, <Kbd>x</Kbd> seleccionar</span>}
        actions={<NewCaseButton />} />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-60 max-w-md">
          <Icono nombre='mdi:magnify' className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar radicado, asunto, ciudadano…" className="w-full pl-8" aria-label="Buscar en la bandeja" />
        </div>
        <ToggleGroup type="single" variant="outline" aria-label="Vistas rápidas" value={filters.view ?? ''}
          onValueChange={(k) => setFilters(k ? { q: filters.q, sort: filters.sort, dir: filters.dir, view: k } : { q: filters.q, sort: filters.sort, dir: filters.dir })}>
          {Object.entries(QUICK_VIEWS).map(([k, v]) => <ToggleGroupItem key={k} value={k}>{v.label}</ToggleGroupItem>)}
        </ToggleGroup>
        <SavedViewsMenu currentParams={toSearchParams(filters).toString()} currentColumns={columns} onApply={applyView} notify={notify} />
        <Tooltip>
          <TooltipTrigger asChild><Button variant="ghost" size="icon" aria-label="Exportar CSV (todo lo cargado)" onClick={() => exportCsv(rows)} disabled={!rows.length}><Icono nombre='mdi:download' /></Button></TooltipTrigger>
          <TooltipContent side="bottom">Exportar CSV</TooltipContent>
        </Tooltip>
      </div>
      <InboxFilterBar value={filters} onChange={setFilters} />
      {selected.length > 0 && <InboxBulkActions selected={selected} onDone={() => setSelectedIds([])} onExport={() => exportCsv(selected)} notify={notify} />}

      {query.isLoading ? <LoadingState rows={10} />
        : query.isError ? <ErrorState error={query.error} onRetry={() => void query.refetch()} title="No pudimos cargar la bandeja." />
        : rows.length === 0 ? <EmptyState title={activeCount || filters.q ? 'Ningún caso coincide con estos filtros.' : 'Aún no hay casos en la bandeja.'} hint={activeCount || filters.q ? 'Prueba quitando algún filtro o ampliando la búsqueda.' : 'Cuando lleguen solicitudes por cualquier canal aparecerán aquí.'} action={activeCount ? <Button variant="outline" size="sm" onClick={() => setFilters({ q: filters.q })}>Limpiar filtros</Button> : <NewCaseButton />} />
        : (
          <Tabla
            columnas={columnas} filas={rows} claveFila={(r) => r.id} onClicFila={(r) => nav(`/pqrs/${r.id}`)} buscar={false} densa
            orden={orden} onOrdenChange={onOrdenChange}
            masFilas={{ hayMas: !!query.hasNextPage, cargando: query.isFetchingNextPage, onCargar: () => void query.fetchNextPage() }}
            seleccion={selectedIds} onSeleccionChange={setSelectedIds}
            columnasVisibles={columnasActivas} onColumnasVisiblesChange={setColumns} columnasPorDefecto={DEFAULT_COLUMNS}
            virtual={{ alto: 'calc(100vh - 19rem)' }}
            filaActiva={focus >= 0 ? rows[focus]?.id ?? null : null}
          />
        )}
    </div>
  );
}

/** Dentro de la etiqueta «En triaje»: la petición quedó marcada para que alguien la revise antes de seguir. */
function ReviewIcon() {
  return (
    <Tooltip>
      <TooltipTrigger asChild><span className="inline-flex shrink-0 text-warning"><Icono nombre="clarity:warning-solid" etiqueta="Requiere revisión" /></span></TooltipTrigger>
      <TooltipContent side="bottom">Requiere revisión</TooltipContent>
    </Tooltip>
  );
}

function Cell({ row, col }: { row: CaseRow; col: string }) {
  switch (col) {
    case 'radication': return <span className="tnum">{row.radication ?? <span className="text-muted-foreground">Pendiente</span>}</span>;
    case 'createdAt': return <span title={fmtDate(row.createdAt)}>{fmtDate(row.createdAt, false)}</span>;
    case 'citizenName': return row.anonymous ? <span className="text-muted-foreground">Anónimo</span> : <span className="inline-flex items-center gap-1">{row.citizenImportant && <ImportantIcon />}{row.citizenName ?? '—'}</span>;
    case 'subject': return <span title={row.subject}>{row.subject}</span>;
    case 'priority': return <PriorityTag priority={row.priority} />;
    case 'due': return <DueBadge due={row.due} dueAt={row.dueAt} compacto />;
    case 'status': return <StatusTag status={row.status}>{row.requiresReview && <ReviewIcon />}</StatusTag>;
    case 'channel': return <>{CHANNEL_LABEL[row.channel] ?? row.channel}</>;
    case 'assigneeName': return row.assigneeName ? <>{row.assigneeName}</> : <span className="text-warning">Sin asignar</span>;
    case 'rastreo': return <RastreoTag rastreo={row.rastreo} />;
    default: { const v = (row as unknown as Record<string, unknown>)[col]; return <>{v === null || v === undefined ? <span className="text-muted-foreground">—</span> : String(v)}</>; }
  }
}
function cellText(r: CaseRow, col: string): string {
  switch (col) {
    case 'createdAt': return fmtDate(r.createdAt);
    case 'citizenName': return r.anonymous ? 'Anónimo' : r.citizenName ?? '';
    case 'priority': return PRIORITY_LABEL[r.priority] ?? r.priority;
    case 'due': return r.due?.text ?? '';
    case 'status': return STATUS_LABEL[r.status] ?? r.status;
    case 'channel': return CHANNEL_LABEL[r.channel] ?? r.channel;
    case 'rastreo': return r.rastreo ? `${CERTIFIED_STATE_LABEL[r.rastreo.estado] ?? r.rastreo.estado}${r.rastreo.total > 1 ? ` (${r.rastreo.entregados}/${r.rastreo.total})` : ''}` : '';
    default: { const v = (r as unknown as Record<string, unknown>)[col]; return v === null || v === undefined ? '' : String(v); }
  }
}
